import type {ReactElement} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import type {AiPromptBoxUploadsProps} from './ai-prompt-box-uploads.types';
import {AttachmentOptionType} from './ai-prompt-box-uploads.types';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {openDesignSelectorModal} from '@Modals/design-selector-modal/design-selector-modal';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import styles from './ai-prompt-box-uploads.module.scss';
import actionButtonStyles from '../../../ai-prompt-box-action-button.module.scss';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {authenticateUserForAiChat, openAttachTemplateGalleryDialog} from '@Libraries/ai-chat.library';
import type {ElementData, ImageData} from '@Libraries/add-media-library';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import type {DesignAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentDivider, PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {
  convertElementDataToAttachment,
  LoadingAttachmentsTracker,
  MAX_ATTACHMENTS,
  validatePresetsInBackground,
} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES} from '@Components/ai-prompt-box/libraries/ai-prompt-box-drag-drop.library';
import {useAiPromptBoxDragDropUploader} from '@Components/ai-prompt-box/hooks/useAiPromptBoxDragDropUploader';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {openRecordImageAndVideoModal} from '@Modals/record-image-and-video-modal';
import {RecorderMediaType} from '@Panels/record-image-and-video-panel/record-image-and-video.types';
import {isOpenCustomImageAndVideoRecorder} from '@Libraries/record-image-and-video.library';
import {useHiddenImageAndVideoInput} from '@Hooks/poster-editor/useHiddenImageAndVideoInput';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {Text, TextSize} from '@Components/text';
import type {OverlayChildren} from 'react-bootstrap/Overlay';
import {getPosterThumbnailImagePreviewURL} from '@Libraries/poster-library';
import {DesignSelectorTab, LimitExceededAction} from '@Components/design-selector/design-selector.types';
import {ToastType} from '@Components/toast/toast.types';
import {hideLoading} from '@Libraries/loading-toast-library';
import {UPLOAD_LOADING_KEY} from '@Libraries/user-media-library';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {useAppDispatch} from '@/hooks';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {isAiChatSource} from '@Components/ai-chat-window/ai-chat-window.types';

/**
 * Resolves limit-exceeded events for design selection.
 * If the click delta itself exceeds the cap (e.g. Select All on a 10-page design when
 * cap is 6), even evicting all existing selections can't make room — reject and toast.
 * Otherwise evict oldest existing items to make room.
 */
function resolveDesignLimitExceeded(info: {newlyAdded: unknown[]; maxSelection: number}, maxAllowed: number): LimitExceededAction {
  if (info.newlyAdded.length > info.maxSelection) {
    window.PMW.showToast({
      duration: 3000,
      title: window.i18next.t('pmwjs_upto_x_attachments_allowed', {number: maxAllowed}),
      type: ToastType.WARNING,
      useMaxZIndex: true,
    });
    return LimitExceededAction.REJECT;
  }
  return LimitExceededAction.EVICT_OLDEST;
}

export function AiPromptBoxUploads(props: AiPromptBoxUploadsProps): ReactElement {
  const {state, actions} = useAiPromptBox(props.promptBoxId);
  const uploadsDropdownRef = useRef<HTMLDivElement>(null);
  const [showUploadsDropdown, setShowUploadsDropdown] = useState(false);
  const CONTEXTUAL_MENU_POPUP_OFFSET = 4;
  const maxAttachments = props.maxAttachments ?? MAX_ATTACHMENTS;
  const attachmentsRef = useRef(state.attachments);
  const onTemplateSelectedRef = useRef<((e: CustomEvent<{id: string; idPage: string}>) => void) | undefined>(undefined);
  const uploadsPanelId = 'ai-attachments-panel';
  const dispatch = useAppDispatch();
  const {handleFiles: handleDeviceFiles} = useAiPromptBoxDragDropUploader(props.promptBoxId);

  const onAddRecordedItem = (item: ElementData): void => {
    const currentAttachmentCount = attachmentsRef.current?.items.length ?? 0;
    if (currentAttachmentCount >= maxAttachments) {
      return;
    }

    const tracker = new LoadingAttachmentsTracker();
    const newIds = actions.addLoadingAttachments(1);
    tracker.addIds(newIds);

    try {
      const newAttachment = convertElementDataToAttachment(item, PromptAttachmentType.CAPTURE_PHOTO);
      const {idsToRemove, activeIds} = tracker.resolveIds(1);
      actions.removeLoadingAttachments(idsToRemove);

      if (activeIds.length > 0) {
        actions.addAttachments([newAttachment]);
      }
    } catch {
      actions.removeLoadingAttachments(tracker.resolveAllOnError());
    }
  };

  const hiddenImageAndVideoInput = useHiddenImageAndVideoInput(onAddRecordedItem);

  const trackAttachmentOptionClicked = (type: AttachmentOptionType): void => {
    if (isAiChatSource(state.source)) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_ATTACHMENTS, {[GA4EventParamName.TYPE]: type});
    }
  };

  const sizeClassMap = {
    [AiPromptBoxSize.SMALL]: actionButtonStyles.actionButtonSmall,
    [AiPromptBoxSize.MEDIUM]: actionButtonStyles.actionButtonMedium,
    [AiPromptBoxSize.SINGLE_LINE]: actionButtonStyles.actionButtonSingleLine,
    [AiPromptBoxSize.SMALL_EXTENDED]: actionButtonStyles.actionButtonSmall,
  };

  const iconSizeClassMap = {
    [AiPromptBoxSize.SMALL]: IconSize.SIZE_ICON_16,
    [AiPromptBoxSize.MEDIUM]: IconSize.SIZE_ICON_20,
    [AiPromptBoxSize.SINGLE_LINE]: IconSize.SIZE_ICON_16,
    [AiPromptBoxSize.SMALL_EXTENDED]: IconSize.SIZE_ICON_16,
  };

  useEffect(() => {
    attachmentsRef.current = state.attachments;
    const currentAttachmentCount = state.attachments?.items.length ?? 0;
    if (currentAttachmentCount >= maxAttachments && showUploadsDropdown) {
      setShowUploadsDropdown(false);
    }
  }, [state.attachments, showUploadsDropdown]);

  const hideUploadToast = useCallback((): void => {
    hideLoading(UPLOAD_LOADING_KEY);
  }, []);

  useEffect(() => {
    const loadingCount = state.attachmentLoaders?.length ?? 0;
    if (loadingCount > 0) {
      hideUploadToast();
    }
  }, [state.attachmentLoaders, hideUploadToast]);

  useEffect(() => {
    const listener = (e: MouseEvent | TouchEvent): void => {
      const target = e.target as HTMLElement;

      if (!uploadsDropdownRef.current?.contains(target)) {
        setShowUploadsDropdown(false);
      }
    };

    const galleryDesignClickedEvent = 'gallery-design-clicked';
    const galleryDesignClickedHandler = (e: Event): void => {
      onTemplateSelectedRef.current?.(e as CustomEvent<{id: string; idPage: string}>);
    };
    window.addEventListener(galleryDesignClickedEvent, galleryDesignClickedHandler);
    document.addEventListener('touchstart', listener);
    document.addEventListener('mousedown', listener);

    return (): void => {
      window.removeEventListener(galleryDesignClickedEvent, galleryDesignClickedHandler);
      document.removeEventListener('touchstart', listener);
      document.removeEventListener('mousedown', listener);
    };
  }, []);

  const handleUploadClick = (): void => {
    const currentAttachmentCount = attachmentsRef.current?.items.length ?? 0;

    if (currentAttachmentCount >= (maxAttachments satisfies number)) {
      return;
    }

    const tracker = new LoadingAttachmentsTracker();

    openUserMediaModal({
      mediaTypesToShow: [UploadsMediaType.IMAGE],
      showVectors: false,
      uploadAcceptedMimetype: 'image/jpeg,image/png,image/webp',
      defaultTab: UploadsMediaType.IMAGE,
      onStartAddSelected: (numItems?: number): void => {
        if (numItems && numItems > 0) {
          const newIds = actions.addLoadingAttachments(numItems);
          tracker.addIds(newIds);
        }
      },
      onUploadError: (): void => {
        const {idsToRemove} = tracker.resolveIds(1);
        actions.removeLoadingAttachments(idsToRemove);
      },
      onAddSelected: (items: ImageData[]): void => {
        const currentCount = attachmentsRef.current?.items.length ?? 0;
        const {idsToRemove, activeIds} = tracker.resolveIds(items.length);
        actions.removeLoadingAttachments(idsToRemove);

        if (currentCount >= (maxAttachments satisfies number) || items.length === 0 || activeIds.length === 0) {
          return;
        }

        const newAttachments = items.map((item) => convertElementDataToAttachment(item, PromptAttachmentType.IMAGE));
        actions.addAttachments(newAttachments);

        void validatePresetsInBackground(
          newAttachments.map((attachment) => attachment.hashedFilename),
          actions.removeImageAttachments
        );
      },
      maxSelectionNumber: maxAttachments - (attachmentsRef.current?.items.length ?? 0),
    });
  };

  const handleAddDesignClick = (): void => {
    const currentAttachmentCount = attachmentsRef.current?.items.length ?? 0;
    const allowedAttachmentCount = maxAttachments - currentAttachmentCount;

    if (allowedAttachmentCount <= 0) {
      return;
    }

    openDesignSelectorModal({
      designSelectorTabsToShow: [DesignSelectorTab.MY_DESIGNS, DesignSelectorTab.SHARED_WITH_ME],
      maxSelection: allowedAttachmentCount,
      // CodeReviewTaimurSDone: this 3-line block comment at a call site goes against our convention (and what I keep flagging) — pull the reject-vs-evict decision into a small named helper and move this rationale into its jsdoc.
      // CodeReviewTaimurSDone: this suppression only exists because showToast isn't on PMWInterface — type it in global.d.ts (flagged there) and this disable goes away.
      onLimitExceeded: (info) => resolveDesignLimitExceeded(info, allowedAttachmentCount),
      onAddSelected: async (selectedDesigns: DesignSelection[]): Promise<void> => {
        if (selectedDesigns.length === 0) {
          return;
        }

        const tracker = new LoadingAttachmentsTracker();
        const designsToAdd = selectedDesigns.slice(0, allowedAttachmentCount);
        const loadingIds = actions.addLoadingAttachments(designsToAdd.length);
        tracker.addIds(loadingIds);

        const designPromises = designsToAdd.map(async (design) => {
          const previewUrl = await getPosterThumbnailImagePreviewURL(design.posterHashedId, design.pageHashedId);
          return {
            type: PromptAttachmentType.DESIGN,
            designId: design.posterHashedId,
            previewURL: previewUrl,
            pageHashedId: design.pageHashedId,
          } as DesignAttachment;
        });

        try {
          const newDesignAttachments = await Promise.all(designPromises);
          const {idsToRemove, activeIds} = tracker.resolveIds(newDesignAttachments.length);
          actions.removeLoadingAttachments(idsToRemove);

          if (activeIds.length > 0) {
            actions.addAttachments(newDesignAttachments.slice(0, activeIds.length));
          }
        } catch {
          actions.removeLoadingAttachments(tracker.resolveAllOnError());
        }
      },
      selectedDesigns: [],
    });
  };

  const getDesignTemplatePreviews = async (posterHashedId: string, pageHashedId: string): Promise<void> => {
    const currentAttachmentCount = attachmentsRef.current?.items.length ?? 0;

    if (currentAttachmentCount >= maxAttachments) {
      return;
    }

    actions.setActiveIfNotProcessing();
    const previewURL = await getPosterThumbnailImagePreviewURL(posterHashedId, pageHashedId);
    const newAttachment = {
      type: PromptAttachmentType.DESIGN_TEMPLATE,
      designId: posterHashedId,
      previewURL: previewURL,
      pageHashedId: pageHashedId,
    } as DesignAttachment;

    actions.addDesignTemplateAttachments([newAttachment]);
  };

  onTemplateSelectedRef.current = (e: CustomEvent<{id: string; idPage: string}>): void => {
    const currentAttachmentCount = attachmentsRef.current?.items.length ?? 0;
    if (currentAttachmentCount >= maxAttachments) {
      window.PMW.closeGalleryDialog(window.location.href, false);
      return;
    }

    void getDesignTemplatePreviews(e.detail.id, e.detail.idPage).then(() => {
      if (isAiChatSource(state.source)) {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TEMPLATES_ATTACH, {[GA4EventParamName.TYPE]: 'gallerydialog'});
      }
    });
    window.PMW.closeGalleryDialog(window.location.href, false);
  };

  const handleCapturePhotoClick = (): void => {
    const currentAttachmentCount = attachmentsRef.current?.items.length ?? 0;
    if (currentAttachmentCount >= maxAttachments) {
      return;
    }

    if (isOpenCustomImageAndVideoRecorder()) {
      const tracker = new LoadingAttachmentsTracker();

      openRecordImageAndVideoModal({
        recorderOpenedWithMediaType: RecorderMediaType.IMAGE,
        onAddSelected: (item: ElementData): void => {
          const currentAttachmentCount = attachmentsRef.current?.items.length ?? 0;
          if (currentAttachmentCount >= maxAttachments) {
            actions.removeLoadingAttachments(tracker.resolveAllOnError());
            return;
          }

          try {
            const newAttachment = convertElementDataToAttachment(item, PromptAttachmentType.CAPTURE_PHOTO);
            const {idsToRemove, activeIds} = tracker.resolveIds(tracker.getLoadingIds().length);
            actions.removeLoadingAttachments(idsToRemove);

            if (activeIds.length > 0) {
              actions.addAttachments([newAttachment]);
            }
          } catch {
            actions.removeLoadingAttachments(tracker.resolveAllOnError());
          }
        },
        onStartAddSelected: (numItems?: number): void => {
          if (numItems && numItems > 0) {
            const newIds = actions.addLoadingAttachments(numItems);
            tracker.addIds(newIds);
          }
        },
        hideToggle: true,
        onAddSelectedCTAText: window.i18next.t('pmwjs_upload_image'),
      });
    } else {
      hiddenImageAndVideoInput.handleHiddenInputTagClick(RecorderMediaType.IMAGE);
    }
  };

  const handleAddDesignTemplateClick = (): void => {
    const currentAttachmentCount = attachmentsRef.current?.items.length ?? 0;

    if (currentAttachmentCount >= maxAttachments) {
      return;
    }

    openAttachTemplateGalleryDialog();
  };

  const handleUploadFromDeviceClick = (): void => {
    const currentAttachmentCount = attachmentsRef.current?.items.length ?? 0;
    if (currentAttachmentCount >= maxAttachments) {
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES.join(',');
    input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);

    input.addEventListener('change', () => {
      const files = input.files;
      if (files && files.length > 0) {
        handleDeviceFiles(Array.from(files));
      }
      document.body.removeChild(input);
    });

    input.click();
  };

  const closeMobileDropdownModal = (): void => {
    dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
  };

  const getUploadOptions = (): ControlledListItemType[] => {
    const uploadOptions: ControlledListItemType[] = [];
    const dropdown = state.attachments?.dropdown ?? [];

    const optionOrder: (PromptAttachmentType | PromptAttachmentDivider)[] = [
      PromptAttachmentType.UPLOAD_FROM_DEVICE,
      PromptAttachmentType.CAPTURE_PHOTO,
      PromptAttachmentDivider.HORIZONTAL_LINE,
      PromptAttachmentType.IMAGE,
      PromptAttachmentType.DESIGN,
      PromptAttachmentType.DESIGN_TEMPLATE,
    ];

    optionOrder.forEach((option, index) => {
      if (option === PromptAttachmentDivider.HORIZONTAL_LINE) {
        if (dropdown.includes(PromptAttachmentDivider.HORIZONTAL_LINE)) {
          uploadOptions.push({
            id: `divider-${index}`,
            type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER,
          });
        }
        return;
      }

      if (!dropdown.includes(option)) {
        return;
      }

      switch (option) {
        case PromptAttachmentType.UPLOAD_FROM_DEVICE:
          uploadOptions.push({
            id: `upload-from-device-${index}`,
            text: window.i18next.t('pmwjs_upload_from_device'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            isSelected: false,
            icon: 'icon-upload',
            className: 'radius-8',
            onClick: (): void => {
              trackAttachmentOptionClicked(AttachmentOptionType.FROM_DEVICE);
              handleUploadFromDeviceClick();
              closeMobileDropdownModal();
            },
          });
          break;
        case PromptAttachmentType.CAPTURE_PHOTO:
          uploadOptions.push({
            id: `capture-photo-${index}`,
            text: window.i18next.t('pmwjs_capture_photo'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            isSelected: false,
            icon: 'icon-camera',
            className: 'radius-8',
            onClick: (): void => {
              trackAttachmentOptionClicked(AttachmentOptionType.CAPTURE);
              closeMobileDropdownModal();
              handleCapturePhotoClick();
            },
          });
          break;
        case PromptAttachmentType.IMAGE:
          uploadOptions.push({
            id: `upload-image-${index}`,
            text: window.i18next.t('pmwjs_myuploads'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            isSelected: false,
            icon: 'icon-upload',
            className: 'radius-8',
            onClick: (): void => {
              trackAttachmentOptionClicked(AttachmentOptionType.MY_UPLOADS);
              closeMobileDropdownModal();
              handleUploadClick();
            },
          });
          break;
        case PromptAttachmentType.DESIGN:
          uploadOptions.push({
            id: `add-design-${index}`,
            text: window.i18next.t('pmwjs_my_designs'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            isSelected: false,
            icon: 'icon-folder-2',
            className: 'radius-8',
            onClick: (): void => {
              trackAttachmentOptionClicked(AttachmentOptionType.MY_DESIGN);
              closeMobileDropdownModal();
              void authenticateUserForAiChat(handleAddDesignClick);
            },
          });
          break;
        case PromptAttachmentType.DESIGN_TEMPLATE:
          uploadOptions.push({
            id: `add-design-template-${index}`,
            text: window.i18next.t('pmwjs_design_templates_sentence_case'),
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
            isSelected: false,
            icon: 'icon-designs',
            className: 'radius-8',
            onClick: (): void => {
              trackAttachmentOptionClicked(AttachmentOptionType.TEMPLATES);
              closeMobileDropdownModal();
              handleAddDesignTemplateClick();
            },
          });
          break;
        default:
          console.error(`Upload option not configured for ${option} in upload dropdown`);
          break;
      }
    });

    return uploadOptions;
  };

  const getUploadsContent = (): ReactElement => {
    const items: ControlledListItemType[] = getUploadOptions();

    return <ControlledList className="spacing-p-l-2 spacing-p-r-2 spacing-p-t-2 spacing-p-b-1" id="uploads-list" items={items} />;
  };

  const isAddDisabled = (): boolean => {
    const currentAttachmentCount = state.attachments?.items.length ?? 0;
    const isAtMaxAttachments = currentAttachmentCount >= maxAttachments;
    return isAtMaxAttachments || state.promptBoxState === PromptBoxState.DISABLED || (state.isFinishingUploads ?? false);
  };

  const getAttachmentIcon = (): ReactElement => {
    const isDisabled = isAddDisabled();
    return (
      <div className={`flex-center radius-round ${sizeClassMap[state.size]} ${styles.contextualMenuDropdownSelector} ${isDisabled ? styles.disabled : ''}`}>
        <Icon icon={'icon-plus'} isDisabled={isDisabled} size={iconSizeClassMap[state.size]} className={isDisabled ? styles.disabled : ''} />
      </div>
    );
  };

  const getTooltipText = (): string => {
    const currentAttachmentCount = state.attachments?.items.length ?? 0;
    const isAtMaxAttachments = currentAttachmentCount >= maxAttachments;
    if (isAtMaxAttachments) {
      return window.i18next.t('pmwjs_upto_x_attachments_allowed', {number: maxAttachments});
    }
    return props.defaultTooltipText ?? window.i18next.t('pmwjs_add_reference_images_and_templates');
  }

  const getTooltip = (): OverlayChildren => {
    return (
      <Tooltip className="spacing-m-b-2 _rounded-arrow left">
        <Text val={getTooltipText()} size={TextSize.XSMALL} />
      </Tooltip>
    );
  };

  const getDropdown = (): ReactElement => {
    return (
      <Dropdown
        open={showUploadsDropdown}
        onOpen={() => {
          if (!isAddDisabled()) {
            setShowUploadsDropdown(true);
          }
        }}
        onClose={() => {
          setShowUploadsDropdown(false);
        }}
        doesCloseOnOuterClick
        doCloseOnInnerClick={true}
        selector={getAttachmentIcon()}
        selectorClassNameOnOpen={styles.contextualMenuDropdownSelectorOpen}
        popup={getUploadsContent()}
        popUpHasCustomWidth
        popupClassName={styles.contextualMenuPopup}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        shouldShift={false}
        popupOffset={CONTEXTUAL_MENU_POPUP_OFFSET}
        leftOffset={-10}
        topOffset={10}
        showModalForSmallScreen
        position={DROPDOWN_POSITION.FIXED}
        modalTitle={window.i18next.t('pmwjs_attachments')}
        modalClassName={styles.contextualMenuModal}
        panelIdForModal={uploadsPanelId}
      />
    );
  };

  if (!props.renderAttachmentTooltip) {
    return <div ref={uploadsDropdownRef}>{getDropdown()}</div>;
  }

  return (
    <div ref={uploadsDropdownRef}>
      <OverlayTrigger show={showUploadsDropdown ? false : undefined} overlay={getTooltip()} delay={{show: 200, hide: 200}} placement={'top-start'} trigger={['hover', 'focus']}>
        <div className={styles.overlayTriggerWrapper}>{getDropdown()}</div>
      </OverlayTrigger>
    </div>
  );
}
